import http from './api'
import { Room, StartingPointsEntry } from '../types/room'
import type { RoomPredictionStepApi } from '../types/predictionSteps'
import roomCacheService from './roomCacheService'
import config from './config'

export interface PublicFootballStatus {
  syncEnabled: boolean
  roomLeagues: string[]
  enrolledLeagues: string[]
  missingLeagues: string[]
  fullyEnrolled: boolean
  hasPublicParticipant: boolean
  needsLocation: boolean
}

export interface JoinPublicFootballResult {
  enrolledTournamentIds: string[]
  syncedPredictionCount: number
  publicFootballUrl: string
  alreadyFullyEnrolled: boolean
}

const isDevelopment = config.environment !== 'production'

export interface CreateRoomData {
  name: string
  type: string
  role?: string
  gameType?: 'football' | 'chess' | 'formula1' | 'golf' | 'academ'
  leagues?: string[] // Optional for chess/formula1/academ
  /** Academ learning */
  academSubject?: string
  academCategory?: string
  academDefaultLevel?: number
  academQuestionsPerSession?: number
  setting?: {
    oneZero: string
    twoZero: string
    twoOne: string
    zeroZero: string
    oneOne: string
    twoTwo: string
    threeZero: string
    threeOne: string
    threeTwo: string
    threeThree: string
    fourZero: string
    fourOne: string
    fourTwo: string
    fourThree: string
    fourFour: string
    fiveAndMore: string
    fiveFiveMore: string
    championAtStart?: string
    championSecondChance?: string
    enableFutureChampionStart?: boolean
    enableFutureChampionSecondChance?: boolean
    enableTopScorer?: boolean
    topScorerPoints?: string
    winnerAdditionalTime?: string
    winnerPenalties?: string
  }
  // Chess settings
  defaultTimeControl?: string
  defaultVariant?: string
  defaultStockfishLevel?: number
  defaultRated?: boolean
  chessTournamentType?: string
  // Chess arena branding (when gameType is 'chess' and chessTournamentType is 'arena')
  arenaLogo?: string
  arenaBanner?: string
  arenaPrimaryColor?: string
  arenaSecondaryColor?: string
  arenaAccentColor?: string
  /** Opt-in AI rivalry banter in weekly room digest (football rooms). */
  agentBanterEnabled?: boolean
  // Formula 1 settings (handled separately via formula1-room-settings endpoint)
}

// Cache for user rooms list
let userRoomsCache: { data: Room[]; timestamp: number } | null = null
const USER_ROOMS_CACHE_TTL = 60 * 1000 // 1 minute

class RoomsService {
  async getUserRooms(): Promise<Room[]> {
    // Return cached data if fresh
    if (userRoomsCache && Date.now() - userRoomsCache.timestamp < USER_ROOMS_CACHE_TTL) {
      if (isDevelopment) {
        console.log('📦 Returning cached user rooms')
      }
      return userRoomsCache.data
    }
    
    const response = await http.get('/rooms')
    userRoomsCache = {
      data: response.data,
      timestamp: Date.now()
    }
    
    // Prefetch individual rooms into cache for faster navigation
    roomCacheService.prefetch(response.data)
    
    return response.data
  }

  /**
   * FAST: Get room summary with pre-aggregated player points
   * Use this for initial room load / rankings page
   * Players have totalPoints, predictionCount but empty predictions array
   */
  async getPredictionSteps(roomId: string): Promise<RoomPredictionStepApi[]> {
    const response = await http.get(`/rooms/${roomId}/prediction-steps`)
    const steps = response.data?.steps
    return Array.isArray(steps) ? steps : []
  }

  async getRoomSummary(roomId: string): Promise<Room> {
    const startTime = Date.now()
    const response = await http.get(`/rooms/${roomId}/summary`)
    const duration = Date.now() - startTime
    
    if (isDevelopment) {
      console.log(`🚀 Fetched room summary in ${duration}ms`)
    }
    
    return response.data
  }

  /**
   * Get room with FULL data including all predictions
   * Use this only when viewing player details/stats
   * @param roomId - Room ID to fetch
   * @param options - { skipCache: boolean, onCacheHit: callback, timeout?: number }
   */
  async getRoom(roomId: string, options?: { 
    skipCache?: boolean
    onCacheHit?: (cachedRoom: Room, isFresh: boolean) => void
    timeout?: number // Optional timeout override (default: 45000ms for full room data)
  }): Promise<Room> {
    const { skipCache = false, onCacheHit, timeout = 45000 } = options || {}
    
    // Check cache first (unless skipCache is true)
    if (!skipCache) {
      const cached = roomCacheService.get(roomId)
      if (cached) {
        if (isDevelopment) {
          console.log(`📦 Cache ${cached.isFresh ? 'HIT (fresh)' : 'HIT (stale)'} for room ${roomId}`)
        }
        
        // Notify caller about cache hit immediately
        if (onCacheHit) {
          onCacheHit(cached.data, cached.isFresh)
        }
        
        // If cache is fresh, return it without fetching
        if (cached.isFresh) {
          return cached.data
        }
        
        // If stale, we'll still fetch but caller already has data to display
      }
    }
    
    // Fetch from API with custom timeout for full room data (longer than default)
    const startTime = Date.now()
    const response = await http.get(`/rooms/${roomId}`, {
      timeout: timeout
    })
    const duration = Date.now() - startTime
    
    if (isDevelopment) {
      console.log(`🌐 Fetched full room in ${duration}ms`)
    }
    
    // Update cache
    roomCacheService.set(roomId, response.data)
    
    return response.data
  }

  /**
   * Get cached room synchronously (no network request)
   * Useful for showing UI immediately before async fetch
   */
  getCachedRoom(roomId: string): Room | null {
    const cached = roomCacheService.get(roomId)
    return cached?.data || null
  }

  /**
   * Invalidate room cache (call after updates)
   */
  invalidateRoomCache(roomId: string) {
    roomCacheService.invalidate(roomId)
    // Also invalidate user rooms list
    userRoomsCache = null
  }

  /**
   * Invalidate user rooms list cache so next getUserRooms() refetches from API.
   * Call after create/delete/leave room so dashboard shows up-to-date list.
   */
  invalidateUserRoomsCache() {
    userRoomsCache = null
  }

  async createRoom(data: CreateRoomData): Promise<Room> {
    const response = await http.post('/rooms', data)
    // Invalidate list cache so dashboard refetch shows the new room immediately
    userRoomsCache = null
    return response.data
  }

  async updateRoom(id: string, data: Partial<CreateRoomData>): Promise<Room> {
    const response = await http.patch(`/rooms/${id}`, data)
    userRoomsCache = null
    return response.data
  }

  async deleteRoom(id: string): Promise<void> {
    await http.delete(`/rooms/${id}`)
    userRoomsCache = null
  }

  async joinRoom(roomId: string): Promise<void> {
    await http.post(`/rooms/${roomId}/join`)
    userRoomsCache = null
  }

  async leaveRoom(roomId: string): Promise<void> {
    await http.post(`/rooms/${roomId}/leave`)
    userRoomsCache = null
  }

  async recalculatePoints(roomId: string): Promise<any> {
    // Use longer timeout for recalculation as it processes many predictions
    const response = await http.post(`/rooms/${roomId}/recalculate-points`, {}, {
      timeout: 120000 // 2 minutes timeout for heavy recalculation
    })
    return response.data
  }

  /** Owner / super admin: add a starting-points adjustment (+/-) for a player. */
  async addPlayerStartingPoints(
    roomId: string,
    playerId: string,
    data: { amount: number; note?: string },
  ): Promise<{
    roomId: string
    playerId: string
    entry: StartingPointsEntry
    total: number
  }> {
    const response = await http.post(
      `/rooms/${roomId}/players/${playerId}/starting-points`,
      data,
    )
    return response.data
  }

  /** Any room member: list a player's starting-points adjustment history. */
  async getPlayerStartingPoints(
    roomId: string,
    playerId: string,
  ): Promise<{
    roomId: string
    playerId: string
    entries: StartingPointsEntry[]
    total: number
  }> {
    const response = await http.get(
      `/rooms/${roomId}/players/${playerId}/starting-points`,
    )
    return response.data
  }

  async getPublicFootballStatus(roomId: string): Promise<PublicFootballStatus> {
    const response = await http.get(`/rooms/${roomId}/public-football-status`)
    return response.data
  }

  async joinPublicFootballFromRoom(
    roomId: string,
    body?: { countryCode?: string; city?: string; carryOverPredictions?: boolean },
  ): Promise<JoinPublicFootballResult> {
    const response = await http.post(`/rooms/${roomId}/join-public-football`, body ?? {})
    return response.data
  }
}

const roomsService = new RoomsService()
export default roomsService
